/**
 * W5 AI 补足 · 批量生成 + 策展面板(人物/产品共用,设计 §2.3/§3.2)。
 *
 * 三步一屏:选模式(起单)→ 轮询产物(4s,终态停)→ N 选 M 入库(SlotMaterialsSection
 * 多选态)。大角度漂脸靠人眼挑,机器不兜底 —— 所以产物永远先暂存任务资产,人选了才入库。
 * 数据/权限全走 adapter 注入:面板不 import 任何 entities/features(FSD 底层)。
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, Sparkles } from "lucide-react";

import {
  AUGMENT_TERMINAL,
  type AugmentAsset,
  type AugmentPick,
  type AugmentTask,
} from "@/shared/api/augment";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { SlotMaterialsSection } from "@/shared/ui/slot-materials";
import { Textarea } from "@/shared/ui/textarea";
import { toast } from "@/shared/hooks/use-toast";
import { errorToast } from "@/shared/lib/apiErrors";

// 契约类型下沉 shared/api/augment(entities 服务同源引用);这里 re-export 保持旧 import 面。
export type { AugmentAsset, AugmentTask } from "@/shared/api/augment";

export interface AugmentModeOption {
  key: string;
  label: string;
  hint: string;
  /** 提供即出槽多选(人物 slots 模式);缺省不出。 */
  slotOptions?: { key: string; label: string }[];
  /**
   * 0903 计划出图张数(花钱前算价用;调用方已经把这个数写进 `hint` 文案里,这里原样引一份,
   * 不新发明第二个数)。无 `slotOptions` 时就是这次起单的总张数(如 standardize=3);
   * 有 `slotOptions` 时是「每槽」张数,面板按已勾选槽数相乘(未勾选=全部槽位,与起单语义一致)。
   * 缺省 = 不知道张数,不显示预计消耗(不臆造)。
   */
  estimatedCount?: number;
  /**
   * 提供即出一个多行输入框,内容随起单送到 `adapter.start` 的第三参(0916「从零生成」)。
   *
   * ★这是 AI 补足此前够不到的那一格:另外几个模式都以一张底图做 i2i 锚,而刚建的虚拟人物
   * 名下一张素材都没有 —— "让 AI 给我画一个人"在归一前根本没有入口。有它的模式,
   * 输入为空时「开始生成」按钮禁用(后端也拦,但别让用户白起一单)。
   */
  briefPlaceholder?: string;
}

/** 可选底图(1011:产品补足「选哪张就补哪张」)。url 为现签预览,缺省时只显示标注。 */
export interface AugmentAnchorOption {
  id: number;
  url?: string | null;
  label?: string | null;
}

export interface AugmentAdapter {
  /** `anchorId` 仅在面板给了 `anchorOptions` 时送出(= 用户选中的底图物料 id)。 */
  start: (
    mode: string,
    slots?: string[],
    brief?: string,
    anchorId?: number
  ) => Promise<AugmentTask>;
  fetchTask: (taskId: number) => Promise<AugmentTask>;
  /** 入库结果原样回传给 `onCurated` —— 调用方常要拿第一张去钉主素材(从零生成流)。 */
  curate: (taskId: number, picks: AugmentPick[]) => Promise<{ id: number }[]>;
  /**
   * 最近几单(新→旧,不带产物)。提供即在选模式态出「最近的生成」——
   * 补掉「面板一关,已生成的候选图再也找不回来」那个洞(0916 演示机走查挂账)。
   * 不提供则不出这一块(老调用方行为不变)。
   */
  listTasks?: () => Promise<AugmentTask[]>;
}

// 终态表下沉 shared/api/augment(产品侧「生成中」角标同一口径)
const TERMINAL = AUGMENT_TERMINAL;

/** 关窗后台继续的提示语(进度下方常驻一行 + 生成中关窗的 toast 同一句,别各写各的)。 */
const AUGMENT_BACKGROUND_HINT =
  "可以关闭此窗口,生成会在后台继续,完成后在「AI 补足 › 最近的生成」查看";

/**
 * 1013 生成中关窗不丢单:进行中的 taskId 按 queryKeyBase 记进 sessionStorage。
 *
 * ★此前 taskId 只活在组件 state 里,关窗即 reset —— 一单要跑几分钟,用户要么被弹窗困住,
 * 要么一关就「任务消失」(演示机中国珠宝实见;后端单子其实都成功了)。产品侧面板随侧滑
 * 一起卸载,react-query 缓存过 gc 也会丢,所以落 sessionStorage(隐私窗/被禁时静默降级)。
 */
function activeStorageKey(queryKeyBase: (string | number)[]): string {
  return `augment-active:${JSON.stringify(queryKeyBase)}`;
}

function readActiveTask(key: string): number | null {
  try {
    const v = Number(window.sessionStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeActiveTask(key: string, taskId: number | null) {
  try {
    if (taskId === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, String(taskId));
  } catch {
    // 存不了就只剩「最近的生成」那条路,不影响起单
  }
}

/**
 * 这次起单实际计划出几张(花钱前算价)。缺 `estimatedCount` → null(面板不许自己编数)。
 * 有 `slotOptions` 时 `estimatedCount` 是「每槽」张数,乘以已勾选槽数(未勾选=全部槽位,
 * 与 {@link AugmentAdapter.start} 的 `slots` 参数同一口径)。
 */
/**
 * 全部补足模式的展示名兜底表。
 *
 * ★为什么需要它:历史任务里的 mode 可能**当前不在 `modes` 里** —— 「从零生成」只给
 * 名下还没有素材的虚拟人物出,人物一旦有了素材这个模式就消失,而它起过的历史单还在。
 * 光查 `modes` 会回显裸 key,用户看到的是一行「create · 3 张」(0916 演示机实见)。
 */
const MODE_LABEL_FALLBACK: Record<string, string> = {
  create: "从零生成",
  standardize: "定妆标准化",
  slots: "按槽批量生成",
  whiten: "白底标准化",
  angles: "多角度",
};

/** 任务的 mode → 展示名(认单靠它):先查调用方给的 modes,再查兜底表,都没有才回显 key。 */
function modeLabel(modes: AugmentModeOption[], mode?: string | null): string {
  if (!mode) return "上一单";
  return modes.find((m) => m.key === mode)?.label ?? MODE_LABEL_FALLBACK[mode] ?? mode;
}

/** 起单时刻 → 「09-16 10:30」。解析不出就不显示,不编一个时间。 */
function shortTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function plannedUnitCount(mode: AugmentModeOption, pickedSlots: Set<string>): number | null {
  if (mode.estimatedCount == null) return null;
  if (!mode.slotOptions) return mode.estimatedCount;
  const n = pickedSlots.size > 0 ? pickedSlots.size : mode.slotOptions.length;
  return mode.estimatedCount * n;
}

export function AugmentPanel({
  open,
  onOpenChange,
  title,
  modes,
  slotLabels,
  adapter,
  queryKeyBase,
  onCurated,
  unitCredits,
  anchorOptions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  modes: AugmentModeOption[];
  /** slot key → 展示名(分组标题;ai 预标 slot 没对上时原样展示 key)。 */
  slotLabels: Record<string, string>;
  adapter: AugmentAdapter;
  /** 轮询 query key 前缀(人物/产品各自隔离缓存)。 */
  queryKeyBase: (string | number)[];
  /**
   * 0903:`pack.augment` 每张单价(调用方从 `usePriceBook()` 取,面板本身不 import
   * entities/pricing —— 保持「面板不碰 FSD 底层」的既有规矩)。取不到价传 null/undefined,
   * 面板此时不显示价格徽章(不许显示 0,那会被读成「免费」)。
   */
  unitCredits?: number | null;
  /** 入库成功后回调(调用方刷新物料区);带上入库产生的物料行(从零生成流要拿第一张钉主素材)。 */
  onCurated?: (created: { id: number }[]) => void;
  /**
   * 1011:提供即在选模式态出一排底图缩略图(单选),起单把选中的 id 作 `anchorId` 送出。
   * 默认选第一张(调用方按「主图槽第一张」排在最前,与后端缺省口径一致)。
   * ★此前产品补足界面没有选底图的入口、后端也忽略 anchorMaterialId,永远拿主图槽第一张。
   */
  anchorOptions?: AugmentAnchorOption[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const storageKey = activeStorageKey(queryKeyBase);
  // 重开面板(含侧滑卸载后再开)接着上次没看完的那单
  const [taskId, setTaskIdState] = useState<number | null>(() => readActiveTask(storageKey));
  const setTaskId = (id: number | null) => {
    setTaskIdState(id);
    writeActiveTask(storageKey, id);
  };
  // 同一实例换了人物/产品(queryKeyBase 变)→ 换成那边记着的单,别拿 A 的 taskId 去查 B
  const lastKeyRef = useRef(storageKey);
  useEffect(() => {
    if (lastKeyRef.current === storageKey) return;
    lastKeyRef.current = storageKey;
    setTaskIdState(readActiveTask(storageKey));
  }, [storageKey]);
  const [pickedSlots, setPickedSlots] = useState<Set<string>>(new Set());
  const [brief, setBrief] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [anchorPick, setAnchorPick] = useState<number | null>(null);
  // 选中的底图若已不在候选里(被删/迁走/刚标品牌保护),回落到第一张
  const anchorId =
    anchorOptions && anchorOptions.length > 0
      ? (anchorOptions.find((a) => a.id === anchorPick) ?? anchorOptions[0]).id
      : undefined;
  const reset = () => {
    setTaskId(null);
    setSelected(new Set());
    setPickedSlots(new Set());
    setBrief("");
    setAnchorPick(null);
  };

  /**
   * 最近几单(选模式态才拉)。
   *
   * ★这一块补的是一个真实的白烧:0916 演示机走查时我手滑关掉面板,3 张已经生成好的候选图
   * 就再也回不去了 —— 产物明明还在服务端,只是 taskId 只活在这个组件的 state 里。
   * 面板关闭会 reset,所以"记住上一单"救不了刷新/换页;真正的解法是让服务端把最近几单摆出来。
   */
  const recentQuery = useQuery({
    queryKey: [...queryKeyBase, "augment-recent"],
    queryFn: () => adapter.listTasks!(),
    enabled: open && taskId === null && !!adapter.listTasks,
    staleTime: 10 * 1000,
  });
  const recent = (recentQuery.data ?? []).filter((r) => r.requestedCount > 0);

  const start = useMutation({
    mutationFn: ({
      mode,
      slots,
      brief: b,
      anchorId: a,
    }: {
      mode: string;
      slots?: string[];
      brief?: string;
      anchorId?: number;
    }) =>
      // 没给底图候选的调用方(人物侧)保持三参调用,行为与旧版逐字一致
      a === undefined ? adapter.start(mode, slots, b) : adapter.start(mode, slots, b, a),
    // 1013:起单 pending 中关了面板也照样记下这单(原先迟到的 onSuccess 直接丢弃 = 白烧一单);
    //   下次打开直接回到它,同时刷新「最近的生成」让外面的「生成中」角标立刻亮。
    onSuccess: (res) => {
      setTaskId(res.taskId);
      queryClient.invalidateQueries({ queryKey: [...queryKeyBase, "augment-recent"] });
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "起单失败" })),
  });

  const taskQuery = useQuery({
    queryKey: [...queryKeyBase, "augment-task", taskId],
    queryFn: () => adapter.fetchTask(taskId as number),
    enabled: open && taskId !== null,
    refetchInterval: (q) => (q.state.data && TERMINAL.has(q.state.data.status) ? false : 4000),
  });
  const task = taskQuery.data;
  const succeeded = useMemo(
    () => (task?.assets ?? []).filter((a) => a.status === "SUCCESS" && a.url),
    [task]
  );
  const failedCount = (task?.assets ?? []).filter((a) => a.status === "FAILED").length;
  const grouped = useMemo(() => {
    const bySlot = new Map<string, AugmentAsset[]>();
    for (const a of succeeded) {
      const key = a.slot || "其他";
      bySlot.set(key, [...(bySlot.get(key) ?? []), a]);
    }
    return [...bySlot.entries()];
  }, [succeeded]);

  const curate = useMutation({
    mutationFn: () =>
      adapter.curate(
        taskId as number,
        succeeded
          .filter((a) => selected.has(a.assetId))
          .map((a) => ({ assetId: a.assetId, slot: a.slot || "", label: a.label }))
      ),
    onSuccess: (created) => {
      toast({ title: "已入库", description: `选中的 ${selected.size} 张已落到对应槽位。` });
      queryClient.invalidateQueries({ queryKey: queryKeyBase });
      onCurated?.(created ?? []);
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "入库失败" })),
  });

  const running = task != null && !TERMINAL.has(task.status);
  // 关窗时是否算「还在跑」:刚起单首轮还没取回状态也算(不然一关就丢)
  const inFlight = taskId !== null && (task == null || !TERMINAL.has(task.status));

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          if (inFlight || start.isPending) {
            // 生成中关窗:不丢单,只清本次勾选;告诉用户去哪儿找回
            setSelected(new Set());
            toast({ title: "生成在后台继续", description: AUGMENT_BACKGROUND_HINT });
          } else {
            reset();
          }
        }
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {taskId === null
              ? "选一种补足方式起单;生成的候选图先进暂存区,你挑中的才会入库。"
              : "候选图陆续就绪;大角度可能漂脸 —— 只勾你看着像的,不满意的别入库。"}
          </DialogDescription>
        </DialogHeader>

        {taskId === null ? (
          <div className="space-y-3">
            {recent.length > 0 && (
              <div className="rounded-md border border-dashed border-border p-3">
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  最近的生成 —— 点一条回去挑图入库(产物还在,不用重跑)
                </p>
                <div className="flex flex-col gap-1">
                  {recent.slice(0, 5).map((r) => (
                    <button
                      key={r.taskId}
                      type="button"
                      data-testid={`augment-recent-${r.taskId}`}
                      onClick={() => setTaskId(r.taskId)}
                      className="flex items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {modeLabel(modes, r.mode)} · {r.requestedCount} 张
                        {!TERMINAL.has(r.status) && (
                          <span className="ml-2 inline-flex items-center gap-1 text-primary">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            生成中
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {shortTime(r.createdAt)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {anchorOptions && anchorOptions.length > 0 && (
              <div className="rounded-md border border-border p-3" data-testid="augment-anchors">
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  底图 —— 补足以选中的这张为准
                </p>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="补足底图">
                  {anchorOptions.map((a) => {
                    const on = a.id === anchorId;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        title={a.label ?? undefined}
                        data-testid={`augment-anchor-${a.id}`}
                        onClick={() => setAnchorPick(a.id)}
                        className={`h-16 w-16 shrink-0 overflow-hidden rounded border-2 bg-muted text-[10px] ${
                          on
                            ? "border-primary ring-2 ring-primary/40"
                            : "border-transparent opacity-70"
                        }`}
                      >
                        {a.url ? (
                          <img
                            src={a.url}
                            alt={a.label ?? ""}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="line-clamp-3 px-1">{a.label ?? `#${a.id}`}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {modes.map((mode) => (
              <div key={mode.key} className="rounded-md border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{mode.label}</p>
                    <p className="text-xs text-muted-foreground">{mode.hint}</p>
                  </div>
                  <Button
                    size="sm"
                    disabled={
                      start.isPending || (mode.briefPlaceholder != null && brief.trim() === "")
                    }
                    data-testid={`augment-mode-${mode.key}`}
                    onClick={() =>
                      start.mutate({
                        mode: mode.key,
                        slots: mode.slotOptions
                          ? pickedSlots.size > 0
                            ? [...pickedSlots]
                            : undefined
                          : undefined,
                        brief: mode.briefPlaceholder != null ? brief.trim() : undefined,
                        anchorId,
                      })
                    }
                  >
                    {start.isPending ? "起单中…" : "开始生成"}
                  </Button>
                </div>
                {mode.briefPlaceholder != null && (
                  <Textarea
                    className="mt-2"
                    rows={3}
                    maxLength={1000}
                    value={brief}
                    placeholder={mode.briefPlaceholder}
                    data-testid={`augment-brief-${mode.key}`}
                    aria-label="人物描述"
                    onChange={(e) => setBrief(e.target.value)}
                  />
                )}
                {mode.slotOptions && (
                  <div className="mt-2 flex flex-wrap gap-3">
                    {mode.slotOptions.map((s) => (
                      <label key={s.key} className="flex items-center gap-1.5 text-xs">
                        <Checkbox
                          checked={pickedSlots.has(s.key)}
                          onCheckedChange={(v) =>
                            setPickedSlots((prev) => {
                              const next = new Set(prev);
                              if (v) next.add(s.key);
                              else next.delete(s.key);
                              return next;
                            })
                          }
                        />
                        {s.label}
                      </label>
                    ))}
                    <span className="text-xs text-muted-foreground">不勾 = 全部槽位</span>
                  </div>
                )}
                {/* 0903:花钱前算价 —— 缺单价(旧后端/取失败)或缺计划张数一律不显示,不许编数 */}
                {(() => {
                  const count = plannedUnitCount(mode, pickedSlots);
                  if (count == null || unitCredits == null) return null;
                  return (
                    <p
                      className="mt-2 text-xs text-muted-foreground"
                      data-testid={`augment-cost-${mode.key}`}
                    >
                      {t("augment.estimatedCost", {
                        perUnit: unitCredits,
                        count,
                        total: unitCredits * count,
                      })}
                    </p>
                  );
                })()}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {running && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <span data-testid="augment-progress">
                {succeeded.length}/{task?.requestedCount ?? "…"} 张就绪
                {failedCount > 0 ? ` · ${failedCount} 张失败(不影响其余)` : ""}
                {running ? " · 生成中…" : " · 已完成"}
              </span>
            </div>
            {running && (
              <p
                className="-mt-2 text-xs text-muted-foreground"
                data-testid="augment-background-hint"
              >
                {AUGMENT_BACKGROUND_HINT}
              </p>
            )}

            {grouped.length === 0 ? (
              <p className="py-8 text-center text-xs text-muted-foreground">
                {running ? "第一张就绪后会出现在这里…" : "这单没有可用产物(费用只按成功张数计)。"}
              </p>
            ) : (
              grouped.map(([slot, assets]) => (
                <SlotMaterialsSection
                  key={slot}
                  title={slotLabels[slot] ?? slot}
                  hint="点选要入库的候选图"
                  canWrite={false}
                  selectable
                  selectedIds={selected}
                  onToggleSelect={(m) =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (next.has(m.id)) next.delete(m.id);
                      else next.add(m.id);
                      return next;
                    })
                  }
                  materials={assets.map((a) => ({
                    id: a.assetId,
                    label: a.label,
                    url: a.url,
                  }))}
                />
              ))
            )}

            <div className="flex items-center justify-between border-t border-border pt-3">
              <Button variant="ghost" size="sm" onClick={reset}>
                ← 换个方式再来一单
              </Button>
              <Button
                size="sm"
                disabled={selected.size === 0 || curate.isPending}
                data-testid="augment-curate"
                onClick={() => curate.mutate()}
              >
                {curate.isPending ? "入库中…" : `选中 ${selected.size} 张入库`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
